import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Col,
    FormFeedback,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row
} from "reactstrap";

const LEVELS_WITHOUT_HOREN = ["A1", "A2"];

const normalizeText = (value) => (value || "").trim();

const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());
};

const isValidBirthDate = (value) => {
    if (!value) return false;
    return /^\d{4}-\d{2}-\d{2}$/.test(value);
};

// Restituisce il "macro livello" partendo dal name del Level (es. "B1.3" -> "B1")
const getMacroLevel = (levelName) => {
    if (!levelName) return "";
    const m = String(levelName).trim().match(/^([ABC][12])/);
    return m ? m[1] : String(levelName).trim();
};

const buildInitialForm = (classroom) => {
    const c = classroom?.client || {};
    return {
        result: "PASSED",
        firstname: normalizeText(c.firstname),
        lastname: normalizeText(c.lastname),
        email: normalizeText(c.email),
        birth_place: normalizeText(c.birth_place),
        birth_date: normalizeText(c.birth_date),
        scores: {
            lesen: "",
            sprechen: "",
            schreiben: "",
            horen: ""
        }
    };
};

const CompleteIndividualClassroomModal = ({ isOpen, toggle, classroom, onSubmit }) => {
    const [step, setStep] = useState(1);
    const [form, setForm] = useState({});
    const [submitError, setSubmitError] = useState("");

    const macroLevel = useMemo(() => getMacroLevel(classroom?.level?.name), [classroom]);
    const showHoren = useMemo(() => !LEVELS_WITHOUT_HOREN.includes(macroLevel.toUpperCase()), [macroLevel]);

    // Livello finale: chiede anagrafica + voti e genera attestato.
    // Modulo intermedio: chiede solo l'esito PASSED/NOT_PASSED.
    const isFinalModule = !!classroom?.level?.is_final_module;

    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setSubmitError("");
            setForm(buildInitialForm(classroom));
        }
    }, [isOpen, classroom]);

    const updateField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const updateScore = (key, value) => {
        let parsed = value;

        if (parsed !== "") {
            parsed = Number(parsed);
            if (Number.isNaN(parsed)) parsed = "";
            if (parsed !== "" && parsed < 1) parsed = 1;
            if (parsed !== "" && parsed > 10) parsed = 10;
        }

        setForm((prev) => ({
            ...prev,
            scores: { ...prev.scores, [key]: parsed }
        }));
    };

    const validateStep1 = () => {
        if (form.result !== "PASSED" && form.result !== "NOT_PASSED") {
            setSubmitError("Seleziona un esito valido.");
            return false;
        }

        // Modulo intermedio: basta l'esito
        if (!isFinalModule) {
            setSubmitError("");
            return true;
        }

        if (!normalizeText(form.firstname)) {
            setSubmitError("Il nome è obbligatorio.");
            return false;
        }

        if (!normalizeText(form.lastname)) {
            setSubmitError("Il cognome è obbligatorio.");
            return false;
        }

        if (!isValidEmail(form.email)) {
            setSubmitError("Inserisci un'email valida.");
            return false;
        }

        if (!normalizeText(form.birth_place)) {
            setSubmitError("Il luogo di nascita è obbligatorio.");
            return false;
        }

        if (!isValidBirthDate(form.birth_date)) {
            setSubmitError("La data di nascita deve essere nel formato YYYY-MM-DD.");
            return false;
        }

        setSubmitError("");
        return true;
    };

    const validateStep2 = () => {
        if (form.result !== "PASSED") {
            setSubmitError("");
            return true;
        }

        const requiredKeys = showHoren
            ? ["lesen", "sprechen", "schreiben", "horen"]
            : ["lesen", "sprechen", "schreiben"];

        for (const key of requiredKeys) {
            const value = form.scores?.[key];
            if (value === "" || value === null || typeof value === "undefined") {
                setSubmitError("Tutti i voti sono obbligatori.");
                return false;
            }

            const num = Number(value);
            if (Number.isNaN(num) || num < 1 || num > 10) {
                setSubmitError("I voti devono essere compresi tra 1 e 10.");
                return false;
            }
        }

        setSubmitError("");
        return true;
    };

    const goNext = () => {
        if (step === 1 && !validateStep1()) return;

        // Niente step voti se NOT_PASSED oppure modulo intermedio -> conferma diretta
        if (step === 1 && (form.result === "NOT_PASSED" || !isFinalModule)) {
            handleConfirm();
            return;
        }

        setStep((prev) => prev + 1);
    };

    const goBack = () => {
        setSubmitError("");
        setStep((prev) => prev - 1);
    };

    const buildPayload = () => {
        // Modulo intermedio: serve solo l'esito
        if (!isFinalModule) {
            return {
                id_classroom_individual: classroom.id,
                enrollment: {
                    result: form.result
                }
            };
        }

        const payload = {
            id_classroom_individual: classroom.id,
            enrollment: {
                firstname: normalizeText(form.firstname),
                lastname: normalizeText(form.lastname),
                email: normalizeText(form.email),
                birth_place: normalizeText(form.birth_place),
                birth_date: normalizeText(form.birth_date),
                result: form.result
            }
        };

        if (form.result === "PASSED") {
            payload.enrollment.scores = {
                lesen: Number(form.scores.lesen),
                sprechen: Number(form.scores.sprechen),
                schreiben: Number(form.scores.schreiben),
                ...(showHoren ? { horen: Number(form.scores.horen) } : {})
            };
        }

        return payload;
    };

    const handleConfirm = () => {
        if (step === 2 && !validateStep2()) return;

        const payload = buildPayload();
        if (typeof onSubmit === "function")
            onSubmit(payload);
    };

    const renderStep1 = () => (
        <div>
            <Alert color="info" className="mb-3">
                {isFinalModule
                    ? <span><b>Step 1{form.result === "PASSED" ? " di 2" : ""}</b> — Verifica/correggi i dati anagrafici e seleziona l'esito.</span>
                    : <span><b>Modulo intermedio</b> — Seleziona l'esito del modulo. Voti e attestato verranno gestiti al modulo finale del livello.</span>}
            </Alert>

            <div className="border rounded p-3 mb-3">
                <h6 className="mb-3">Esito del corso</h6>
                <div className="d-flex gap-3">
                    <div className="form-check">
                        <Input
                            type="radio"
                            name="result"
                            id="result-passed"
                            checked={form.result === "PASSED"}
                            onChange={() => updateField("result", "PASSED")}
                            className="form-check-input"
                        />
                        <Label htmlFor="result-passed" className="form-check-label">
                            <b className="text-success">PASSATO</b> — il corsista ha completato e supera il corso
                        </Label>
                    </div>
                    <div className="form-check">
                        <Input
                            type="radio"
                            name="result"
                            id="result-notpassed"
                            checked={form.result === "NOT_PASSED"}
                            onChange={() => updateField("result", "NOT_PASSED")}
                            className="form-check-input"
                        />
                        <Label htmlFor="result-notpassed" className="form-check-label">
                            <b className="text-danger">NON PASSATO</b> — il corsista non ha superato il corso
                        </Label>
                    </div>
                </div>
            </div>

            {(!isFinalModule) ? null : <div className="border rounded p-3">
                <h6 className="mb-3">Dati anagrafici</h6>
                <Row className="g-3">
                    <Col md={6}>
                        <Label className="form-label">Nome</Label>
                        <Input
                            value={form.firstname || ""}
                            invalid={!normalizeText(form.firstname)}
                            onChange={(e) => updateField("firstname", e.target.value)}
                        />
                        <FormFeedback>Il nome è obbligatorio.</FormFeedback>
                    </Col>
                    <Col md={6}>
                        <Label className="form-label">Cognome</Label>
                        <Input
                            value={form.lastname || ""}
                            invalid={!normalizeText(form.lastname)}
                            onChange={(e) => updateField("lastname", e.target.value)}
                        />
                        <FormFeedback>Il cognome è obbligatorio.</FormFeedback>
                    </Col>
                    <Col md={12}>
                        <Label className="form-label">Email</Label>
                        <Input
                            type="email"
                            value={form.email || ""}
                            invalid={!normalizeText(form.email) || !isValidEmail(form.email)}
                            onChange={(e) => updateField("email", e.target.value)}
                        />
                        <FormFeedback>Inserisci un'email valida.</FormFeedback>
                    </Col>
                    <Col md={6}>
                        <Label className="form-label">Luogo di nascita</Label>
                        <Input
                            value={form.birth_place || ""}
                            invalid={!normalizeText(form.birth_place)}
                            onChange={(e) => updateField("birth_place", e.target.value)}
                        />
                        <FormFeedback>Il luogo di nascita è obbligatorio.</FormFeedback>
                    </Col>
                    <Col md={6}>
                        <Label className="form-label">Data di nascita</Label>
                        <Input
                            type="date"
                            value={form.birth_date || ""}
                            invalid={!normalizeText(form.birth_date) || !isValidBirthDate(form.birth_date)}
                            onChange={(e) => updateField("birth_date", e.target.value)}
                        />
                        <FormFeedback>La data di nascita è obbligatoria.</FormFeedback>
                    </Col>
                </Row>
            </div>}
        </div>
    );

    const renderScoreInput = (scoreKey, label) => {
        const value = form?.scores?.[scoreKey] ?? "";
        const invalid = value === "" || Number(value) < 1 || Number(value) > 10;

        return (
            <Col md={showHoren ? 3 : 4}>
                <Label className="form-label">{label}</Label>
                <Input
                    type="number"
                    min={1}
                    max={10}
                    step={1}
                    value={value}
                    invalid={invalid}
                    onChange={(e) => updateScore(scoreKey, e.target.value)}
                />
                <FormFeedback>Valore obbligatorio da 1 a 10.</FormFeedback>
            </Col>
        );
    };

    const renderStep2 = () => (
        <div>
            <Alert color="info" className="mb-3">
                <b>Step 2 di 2</b> — Inserisci i voti finali del corsista.
                <br />
                Livello classe: <b>{classroom?.level?.name || "-"}</b>
                <br />
                Voti richiesti: <b>{showHoren ? "LESEN / SPRECHEN / SCHREIBEN / HÖREN" : "LESEN / SPRECHEN / SCHREIBEN"}</b>
            </Alert>

            <div className="border rounded p-3">
                <h6 className="mb-3">
                    <b>{form.firstname} {form.lastname}</b>
                    <div className="text-muted small">{form.email}</div>
                    <div className="text-muted small">
                        {form.birth_place} - {form.birth_date}
                    </div>
                </h6>

                <Row className="g-3">
                    {renderScoreInput("lesen", "LESEN")}
                    {renderScoreInput("sprechen", "SPRECHEN")}
                    {renderScoreInput("schreiben", "SCHREIBEN")}
                    {showHoren ? renderScoreInput("horen", "HÖREN") : null}
                </Row>
            </div>
        </div>
    );

    const totalSteps = (form.result === "PASSED" && isFinalModule) ? 2 : 1;

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
            <ModalHeader toggle={toggle}>
                Completa classe individuale
            </ModalHeader>

            <ModalBody>
                <div className="mb-3">
                    <div className="d-flex flex-wrap gap-2">
                        <span className={`badge ${step === 1 ? "bg-primary" : "bg-light text-dark"}`}>{isFinalModule ? "1. Dati ed esito" : "Esito modulo"}</span>
                        {(form.result === "PASSED" && isFinalModule) ? <span className={`badge ${step === 2 ? "bg-primary" : "bg-light text-dark"}`}>2. Votazioni</span> : null}
                    </div>
                </div>

                {submitError ? <Alert color="danger">{submitError}</Alert> : null}

                {step === 1 && renderStep1()}
                {step === 2 && renderStep2()}
            </ModalBody>

            <ModalFooter className="d-flex justify-content-between">
                <div className="text-muted small">
                    Esito: <b>{form.result === "NOT_PASSED" ? "Non passato" : "Passato"}</b>
                </div>

                <div className="d-flex gap-2">
                    <Button color="light" onClick={toggle}>
                        Annulla
                    </Button>

                    {step > 1 ? (
                        <Button color="secondary" onClick={goBack}>
                            Indietro
                        </Button>
                    ) : null}

                    {step < totalSteps ? (
                        <Button color="primary" onClick={goNext}>
                            Avanti
                        </Button>
                    ) : (
                        <Button color="success" onClick={handleConfirm}>
                            Conferma
                        </Button>
                    )}
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default CompleteIndividualClassroomModal;
